import { format } from 'date-fns';
import { useReduxState } from 'hooks';
import { availableCustomAttributesByType } from 'lib/customAttributes';
import { AchievementRenderAddedBy } from 'components/achievements';
import {
  ContentLabel,
  ContentValue,
  DisplayAttachments,
  ModalButton,
  User
} from 'components/common';
import { AttributeDisplayValue } from 'components/common/customAttributes';

import type { IndividualAchievement, UserWithBasicData } from 'shared-types';

interface IndividualAchievementWithAddedByUser
  extends Pick<
    IndividualAchievement,
    | 'attachments'
    | 'comments'
    | 'completionDate'
    | 'customAttributes'
    | 'expirationDate'
    | 'startedAtDate'
    | 'title'
    | 'workload'
  > {
  AddedByUser: UserWithBasicData;
}

interface Props {
  individualAchievement: IndividualAchievementWithAddedByUser;
  user: UserWithBasicData;
}

const AchievementsIndividualViewInfoModal = ({
  individualAchievement,
  user
}: Props) => {
  const { messages, serverConfig } = useReduxState();

  // all custom attributes for individual achievements
  const customAttributes =
    availableCustomAttributesByType({
      serverConfig,
      type: 'individualAchievement'
    }) || [];

  // this individual achievements own custom attributes with values
  const individualCustomAttributes =
    individualAchievement.customAttributes || {};
  const individualCustomAttributeKeys = new Set(
    Object.keys(individualCustomAttributes)
  );

  return (
    <ModalButton
      category="lighter"
      isRounded={true}
      modalProps={{
        closeButton: true,
        title: messages.individualAchievement,
        width: 'medium'
      }}
      size="small"
      style={{ padding: '4px 9px 5px' }}
      text={messages.commonViewAction}
    >
      <div style={{ padding: '10px 5px' }}>
        <div style={{ fontSize: '1.6rem', fontWeight: 700, marginBottom: 10 }}>
          {individualAchievement.title}
        </div>

        <ContentLabel
          horizontal={true}
          label={messages.commonUser}
          spacing={15}
        >
          <User user={user} enableProfileLink={true} />
        </ContentLabel>

        <ContentLabel
          horizontal={true}
          label={messages.commonAddedBy}
          spacing={15}
        >
          {individualAchievement.AddedByUser ? (
            <AchievementRenderAddedBy
              addedBy={{
                ...individualAchievement.AddedByUser,
                addedByType: 'user'
              }}
              enableLink={true}
            />
          ) : (
            '-'
          )}
        </ContentLabel>

        <ContentLabel
          horizontal={true}
          label={messages.commonStartDate}
          spacing={15}
        >
          <ContentValue>
            {individualAchievement.startedAtDate
              ? format(
                  new Date(individualAchievement.startedAtDate),
                  'dd.MM.yyyy'
                )
              : '-'}
          </ContentValue>
        </ContentLabel>

        <ContentLabel
          horizontal={true}
          label={messages.completionsCompletionDate}
          spacing={15}
        >
          <ContentValue>
            {individualAchievement.completionDate
              ? format(
                  new Date(individualAchievement.completionDate),
                  'dd.MM.yyyy'
                )
              : '-'}
          </ContentValue>
        </ContentLabel>

        <ContentLabel
          horizontal={true}
          label={messages.commonExpirationDate}
          spacing={15}
        >
          <ContentValue>
            {individualAchievement.expirationDate
              ? format(
                  new Date(individualAchievement.expirationDate),
                  'dd.MM.yyyy'
                )
              : '-'}
          </ContentValue>
        </ContentLabel>

        <ContentLabel
          horizontal={true}
          label={messages.excelWorkload}
          spacing={15}
        >
          <ContentValue>{individualAchievement.workload || '-'}</ContentValue>
        </ContentLabel>

        <ContentLabel
          horizontal={true}
          label={messages.commonComment}
          spacing={15}
        >
          <ContentValue>
            <div style={{ whiteSpace: 'pre-wrap' }}>
              {individualAchievement.comments || '-'}
            </div>
          </ContentValue>
        </ContentLabel>

        <ContentLabel
          horizontal={true}
          label={messages.commonAttachments}
          spacing={15}
        >
          <ContentValue>
            <DisplayAttachments
              attachments={individualAchievement.attachments}
              renderInPortal={false}
            />
          </ContentValue>
        </ContentLabel>

        {customAttributes.map(({ title, key }) => (
          <ContentLabel key={key} horizontal={true} label={title} spacing={15}>
            <ContentValue>
              <AttributeDisplayValue
                value={
                  individualCustomAttributeKeys.has(key)
                    ? individualCustomAttributes[key]
                    : '-'
                }
              />
            </ContentValue>
          </ContentLabel>
        ))}
      </div>
    </ModalButton>
  );
};

export default AchievementsIndividualViewInfoModal;
